import { InjectProperty } from '@jack-henry/frontend-utils/di';
import { DeepReactive } from '@jack-henry/frontend-utils/lit';
import { TmCheckCapture } from '@treasury/domain-components';
import {
    RemoteDepositCaptureService,
    RemoteDepositCustomer,
    RemoteDepositDataField,
    RemoteDepositLocation,
} from '@treasury/domain/remote-deposit-capture';
import { defaultItemModel } from '@treasury/domain/remote-deposit-capture/data/item.data';
import { MitekCaptureResult, MitekManualCaptureHints } from '@treasury/libraries';
import { LabeledListItem, TmContainer } from '@treasury/presentation';
import { cameraIcon, chevronForwardIcon } from '@treasury/presentation/assets/icons';
import { TmValueChanged } from '@treasury/presentation/components/forms';
import '@treasury/presentation/components/tm-body';
import '@treasury/presentation/components/tm-button';
import '@treasury/presentation/components/tm-button-group';
import '@treasury/presentation/components/tm-currency-field';
import '@treasury/presentation/components/tm-footer';
import { TmListColumnRenderer } from '@treasury/presentation/components/tm-list';
import { TmSearchTermChangeEvent } from '@treasury/presentation/components/tm-list-header';
import '@treasury/presentation/components/tm-text-field';
import { css, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { ProceedToReviewPayload, RemoteDepositEvent } from '../containers/remote-deposit.types';
import { CustomerFilter, RemoteDepositItemVm, SearchCriteria } from '../view-models';

export const tagName = 'deposit-create-step';
/**
 * @fires DepositCreateStep#proceed-to-review - Event fired when the user clicks the review button
 */
@customElement(tagName)
export class DepositCreateStep extends TmContainer {
    @property({ attribute: false })
    public location?: RemoteDepositLocation;

    @property({ attribute: false })
    public dataFields: RemoteDepositDataField[] = [];

    @DeepReactive()
    public formData: RemoteDepositItemVm = new RemoteDepositItemVm(defaultItemModel);

    @InjectProperty()
    declare private readonly remoteDepositCaptureService: RemoteDepositCaptureService;

    @state()
    private customers: RemoteDepositCustomer[] = [];

    @state()
    private selectedCriteria: SearchCriteria = SearchCriteria.CustomerName;

    @state()
    private searchValue = '';

    @state()
    private customerPlaceholder = '';

    @state()
    private showOptions = false;

    public getAmount() {
        return this.formData.amount ? this.formData.amount.toString() : '';
    }

    protected updated(changedProperties: Map<string | number | symbol, unknown>) {
        if (changedProperties.has('selectedCriteria')) {
            this.updateCustomerPlaceholder();
        }

        if (changedProperties.has('selectedCriteria') || changedProperties.has('searchValue')) {
            const { selectedCriteria, searchValue } = this;
            if (selectedCriteria === SearchCriteria.AccountNumber && searchValue.length < 4) {
                return;
            }
            if (searchValue.length < 3) {
                return;
            }
            this.getCustomers({ searchValue, selectedCriteria });
        }
    }

    protected render() {
        return html`
            <tm-section>
                <div class="flex justify-between items-center border-b ml-4 pr-4">
                    <div>
                        <label
                            for="front-image-capture"
                            class="tm-check-capture-label flex items-center grow cursor-pointer rounded text-center"
                            >${cameraIcon}&nbsp;
                        </label>
                        Front
                    </div>
                    <div class="flex items-center text-sm">
                        <tm-check-capture
                            .captureOnStart=${false}
                            id="front-image-capture"
                            checkSide="front"
                            .photo=${this.formData.frontImage}
                            @image-uploaded=${this.receiveImage}
                            @image-capture-failed=${(event: CustomEvent<MitekCaptureResult>) =>
                                this.notificationService.renderError(
                                    new Error(
                                        `Image capture failed: ${MitekManualCaptureHints[event.detail.response.warnings.key as keyof typeof MitekManualCaptureHints]}`
                                    )
                                )}
                        ></tm-check-capture>
                        <button
                            class="action-icon w-4 ml-1"
                            @click=${() => this.clickCheckCapture('front-image-capture')}
                        >
                            ${chevronForwardIcon}
                        </button>
                    </div>
                </div>
                <div class="flex justify-between items-center border-b ml-4 pr-4">
                    <div>
                        <label
                            for="back-image-capture"
                            class="tm-check-capture-label flex items-center grow cursor-pointer rounded text-center"
                            >${cameraIcon}&nbsp;
                        </label>
                        Back
                    </div>
                    <div class="flex items-center text-sm">
                        <tm-check-capture
                            .captureOnStart=${false}
                            id="back-image-capture"
                            checkSide="back"
                            .photo=${this.formData.rearImage}
                            @image-uploaded=${this.receiveImage}
                        ></tm-check-capture>
                        <button
                            class="action-icon w-4 ml-1"
                            @click=${() => this.clickCheckCapture('back-image-capture')}
                        >
                            ${chevronForwardIcon}
                        </button>
                    </div>
                </div>
                <tm-form-row label="Amount">
                    <tm-currency-field
                        class="w-1/3"
                        .initial=${this.getAmount()}
                        @value-changed=${({ detail }: TmValueChanged<string>) => {
                            const { value } = detail;
                            if (!value) {
                                return;
                            }

                            this.formData.amount = parseFloat(value);
                        }}
                    ></tm-currency-field>
                </tm-form-row>
                ${this.renderRequiredDataFields()} ${this.renderOptionalFields()}
            </tm-section>
            <tm-footer
                .buttonConfig=${[
                    {
                        text: 'Add Check',
                        onClick: () => this.onReviewClick(),
                        disabled: this.isReviewDisabled(),
                    },
                ]}
            >
            </tm-footer>
        `;
    }

    private updateCustomerPlaceholder() {
        this.customerPlaceholder = {
            [SearchCriteria.AccountNumber]: 'Search last 4 of account number',
            [SearchCriteria.CustomerNumber]: 'Search customer number',
            [SearchCriteria.CustomerName]: 'Search last name',
        }[this.selectedCriteria];
    }

    private onReviewClick() {
        this.dispatchEvent(
            new CustomEvent<ProceedToReviewPayload>(RemoteDepositEvent.ProceedToReview, {
                detail: this.formData,
            })
        );
    }

    private isReviewDisabled() {
        return !this.formData.isValid();
    }

    private receiveImage({ detail }: CustomEvent) {
        const { checkSide, image } = detail;
        if (checkSide === 'front') {
            this.formData.frontImage = image;
        } else {
            this.formData.rearImage = image;
        }
    }

    private clickCheckCapture(id: string) {
        const checkCapture = this.shadowRoot?.getElementById(id) as TmCheckCapture;
        checkCapture.startCapture();
    }

    private getCustomers(filters: CustomerFilter) {
        // This API call gives the appearance that it allows a user to search for a customer by name, number, or account number, but it actually can only one of those at a time.
        this.tryFetch(
            () =>
                this.remoteDepositCaptureService.getCustomers(
                    this.selectedCriteria === SearchCriteria.AccountNumber
                        ? filters.searchValue
                        : '',
                    this.selectedCriteria === SearchCriteria.CustomerName
                        ? filters.searchValue
                        : '',
                    this.selectedCriteria === SearchCriteria.CustomerNumber
                        ? filters.searchValue
                        : ''
                ),
            customers => {
                this.customers = customers.map(c => new RemoteDepositCustomer(c)) ?? [];
            }
        );
    }

    private handleCustomerFilterButtonClick(e: CustomEvent) {
        this.selectedCriteria = e.detail;
    }

    private renderCustomerColumn(customer: RemoteDepositCustomer) {
        return html`<div class="flex flex-col">
            <span class="font-medium text-sm overflow-hidden text-ellipsis"
                >${customer.firstName} ${customer.lastName}</span
            >
            <span class="text-xs">${customer.customerReference}</span>
        </div>`;
    }

    private renderSelectCustomer() {
        // button array to feed button group with customer name, customer number and account number
        const buttons: LabeledListItem<number>[] = [
            { label: 'Last Name', value: SearchCriteria.CustomerName },
            { label: 'Number', value: SearchCriteria.CustomerNumber },
            { label: 'Account', value: SearchCriteria.AccountNumber },
        ];

        return html`<tm-form-row label="Customer">
            <tm-slider
                header="Select Customer"
                placeholder="Select Customer (optional)"
                .value=${this.formData.customerName}
            >
                <div class="bg-white z-10 px-4 pt-2 pb-0">
                    <tm-button-group
                        .buttons=${buttons}
                        @button-group-click=${this.handleCustomerFilterButtonClick}
                    ></tm-button-group>
                </div>
                <tm-list
                    .columns=${[
                        this.renderCustomerColumn,
                    ] as TmListColumnRenderer<RemoteDepositCustomer>[]}
                    .items=${this.customers}
                    .loading=${this.loading}
                    .searchPlaceholder=${this.customerPlaceholder}
                    .searchFields=${['firstName', 'lastName']}
                    disableTypeToFilter
                    @search-term-change=${(e: TmSearchTermChangeEvent) => {
                        this.searchValue = e.detail.value;
                    }}
                    @select-list-item=${(e: CustomEvent<RemoteDepositCustomer>) => {
                        this.formData.customer = e.detail;
                        e.currentTarget?.dispatchEvent(new CustomEvent('close', { bubbles: true }));
                    }}
                ></tm-list>
            </tm-slider>
        </tm-form-row>`;
    }

    private renderRequiredDataFields() {
        if (!this.formData.requiredDataFields) {
            return nothing;
        }
        return this.formData.requiredDataFields.map(
            field =>
                html` <tm-form-row label=${ifDefined(field.text)}>
                    <tm-text-field
                        class="w-auto"
                        .initial=${field.value}
                        @value-changed=${(e: CustomEvent) => {
                            field.value = e.detail.value;
                            this.requestUpdate();
                        }}
                    ></tm-text-field>
                </tm-form-row>`
        );
    }

    private renderOptionalDataFields() {
        if (!this.formData.optionalDataFields) {
            return nothing;
        }
        return this.formData.optionalDataFields.map(
            field =>
                html` <tm-form-row label=${ifDefined(field.text)}>
                    <tm-text-field
                        class="w-auto"
                        .initial=${field.value}
                        @value-changed=${(e: CustomEvent) => {
                            field.value = e.detail.value;
                        }}
                    ></tm-text-field>
                </tm-form-row>`
        );
    }

    private renderOptionalFields() {
        if (!this.showOptions) {
            return html`<div>
                <omega-button type="link" posture="small" @click=${() => (this.showOptions = true)}
                    >Show options</omega-button
                >
            </div>`;
        }
        return html`${this.renderSelectCustomer()} ${this.renderOptionalDataFields()}
            <div>
                <omega-button type="link" posture="small" @click=${() => (this.showOptions = false)}
                    >Hide options</omega-button
                >
            </div>`;
    }

    public static get styles() {
        return [
            css`
                :host {
                    display: block;
                }
                .select-item {
                    color: var(--brand-primary);
                }
                .action-icon svg {
                    width: 100%;
                    height: auto;
                }
                form {
                    background-color: #fff;
                    height: 100%;
                }
                tm-body {
                    background-color: #fff;
                    height: 100%;
                }
            `,
        ];
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: DepositCreateStep;
    }
}
